import type {ClientToolCall} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {ToolCallResult, ToolDefinition, ToolResultTextContent} from '@microsoft/agent-host-protocol'

import type {AhpDebugEvent} from '@github-ui/copilot-shared-ui/utils/ahp-debug-log'
import {
  buildDebugLogDiagnostics,
  buildDebugLogSuggestedRanges,
  isImportantDebugLogSummary,
  summarizeDebugEvent,
  type DebugLogSummary,
} from './ahp-debug-log-summary'
import {formatDebugEvent} from '@github-ui/copilot-shared-ui/utils/ahp-debug-log'

export const AHP_DEBUG_LOG_TOOL_NAME = 'read_ahp_debug_log'
export const AHP_DEBUG_LOG_MAX_LINES = 200
const MAX_SNAPSHOTS = 3

export const AHP_DEBUG_LOG_TOOL_DEFINITION: ToolDefinition = {
  name: AHP_DEBUG_LOG_TOOL_NAME,
  title: 'Read AHP debug log',
  description:
    "Reads this current chat's browser-side AHP debug log. You MUST only invoke this tool when the user " +
    "explicitly asks in the current conversation to inspect or debug this chat's AHP logs. Never invoke it " +
    'proactively, for routine troubleshooting, or based only on an observed error. Call operation=overview ' +
    'first to freeze a snapshot and discover useful line ranges. Then use operation=timeline for compact AHP ' +
    'events and operation=lines for a bounded raw range from that same snapshot. Before interpreting the results, ' +
    'fetch and follow the ahp-debug-log-tracing skill from github/github-ui without cloning the repository. Run ' +
    "exactly: gh api -H 'Accept: application/vnd.github.raw+json' " +
    "'repos/github/github-ui/contents/.github/skills/ahp-debug-log-tracing/SKILL.md?ref=main'",
  inputSchema: {
    type: 'object',
    properties: {
      operation: {
        type: 'string',
        enum: ['overview', 'timeline', 'lines'],
        description: 'Start with overview. Timeline and lines require the returned snapshot_id.',
      },
      snapshot_id: {
        type: 'string',
        description: 'Immutable snapshot id returned by overview.',
      },
      start_line: {
        type: 'integer',
        minimum: 1,
        description: 'First raw log line to include.',
      },
      end_line: {
        type: 'integer',
        minimum: 1,
        description: `Last raw log line to include. A single request can return at most ${AHP_DEBUG_LOG_MAX_LINES} lines.`,
      },
      mode: {
        type: 'string',
        enum: ['important', 'errors', 'all'],
        description: 'Timeline filter. Defaults to important.',
      },
      terms: {
        type: 'array',
        maxItems: 10,
        items: {type: 'string'},
        description: 'Optional action, source, turn id, tool-call id, pending-message id, or text filters.',
      },
    },
    required: ['operation'],
  },
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
  },
}

interface DebugLogSnapshot {
  id: string
  events: readonly AhpDebugEvent[]
  lines: readonly string[]
  summaries: readonly DebugLogSummary[]
}

export interface AhpDebugLogToolState {
  nextSnapshotNumber: number
  snapshots: Map<string, DebugLogSnapshot>
}

type DebugLogOperation = 'overview' | 'timeline' | 'lines'
type TimelineMode = 'important' | 'errors' | 'all'

export function createAhpDebugLogToolState(): AhpDebugLogToolState {
  return {nextSnapshotNumber: 1, snapshots: new Map()}
}

export function fulfilAhpDebugLogTool(
  params: Record<string, unknown>,
  call: ClientToolCall,
  debugEvents: readonly AhpDebugEvent[],
  state: AhpDebugLogToolState,
): ToolCallResult {
  try {
    const operation = parseOperation(params.operation)
    if (operation === 'overview') return createOverview(call, debugEvents, state)

    const snapshot = requireSnapshot(params.snapshot_id, state)
    return operation === 'timeline' ? readTimeline(params, snapshot) : readLines(params, snapshot)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return {
      success: false,
      pastTenseMessage: 'Could not read the AHP debug log',
      error: {message},
      content: [textContent(message)],
    }
  }
}

function parseOperation(value: unknown): DebugLogOperation {
  if (value === 'overview' || value === 'timeline' || value === 'lines') return value
  throw new Error('operation must be one of: overview, timeline, lines')
}

function createOverview(
  call: ClientToolCall,
  debugEvents: readonly AhpDebugEvent[],
  state: AhpDebugLogToolState,
): ToolCallResult {
  const cutoff = debugEvents.findIndex(event => event.message.includes(call.toolCallId))
  const events = debugEvents.slice(0, cutoff >= 0 ? cutoff : debugEvents.length)
  const id = `ahp-debug-${state.nextSnapshotNumber++}`
  const snapshot: DebugLogSnapshot = {
    id,
    events,
    lines: events.map(formatDebugEvent),
    summaries: events.flatMap((event, index) => {
      const summary = summarizeDebugEvent(event, index + 1)
      return summary ? [summary] : []
    }),
  }
  state.snapshots.set(id, snapshot)
  while (state.snapshots.size > MAX_SNAPSHOTS) {
    const oldestId = state.snapshots.keys().next().value
    if (typeof oldestId !== 'string') break
    state.snapshots.delete(oldestId)
  }

  const diagnostics = buildDebugLogDiagnostics(snapshot.summaries)
  const suggestedRanges = buildDebugLogSuggestedRanges(snapshot.lines.length, diagnostics)
  const structuredContent = {
    snapshot_id: id,
    line_range: snapshot.lines.length > 0 ? {start_line: 1, end_line: snapshot.lines.length} : null,
    total_lines: snapshot.lines.length,
    timestamp_range: events.length > 0 ? {start: events[0]?.timestamp, end: events.at(-1)?.timestamp} : null,
    diagnostics,
    suggested_ranges: suggestedRanges,
  }

  const text = [
    `Frozen AHP debug-log snapshot ${id} with ${snapshot.lines.length} raw lines.`,
    snapshot.lines.length > 0 ? `Available raw range: 1-${snapshot.lines.length}.` : 'The snapshot is empty.',
    ...diagnostics.map(
      diagnostic => `Diagnostic${diagnostic.line ? ` at line ${diagnostic.line}` : ''}: ${diagnostic.message}`,
    ),
    ...suggestedRanges.map(range => `Suggested range ${range.start_line}-${range.end_line}: ${range.reason}.`),
    'Use this snapshot_id with operation=timeline before requesting raw lines.',
  ].join('\n')

  return {
    success: true,
    pastTenseMessage: `Froze ${snapshot.lines.length} AHP debug-log lines`,
    structuredContent,
    content: [textContent(text)],
  }
}

function readTimeline(params: Record<string, unknown>, snapshot: DebugLogSnapshot): ToolCallResult {
  const mode = parseTimelineMode(params.mode)
  const terms = parseTerms(params.terms)
  const {startLine, endLine} = parseOptionalRange(params, snapshot.lines.length)
  const matching = snapshot.summaries.filter(summary => {
    if (summary.rawLine < startLine || summary.rawLine > endLine) return false
    if (mode === 'important' && !isImportantDebugLogSummary(summary)) return false
    if (mode === 'errors' && !summary.isError) return false
    if (terms.length === 0) return true
    const searchable = summary.text.toLowerCase()
    return terms.some(term => searchable.includes(term))
  })
  const entries = matching.slice(0, AHP_DEBUG_LOG_MAX_LINES).map(summary => ({
    raw_line: summary.rawLine,
    text: summary.text,
  }))
  const truncated = matching.length > entries.length
  const structuredContent = {
    snapshot_id: snapshot.id,
    requested_range: {start_line: startLine, end_line: endLine},
    mode,
    entries,
    truncated,
  }
  const lines = entries.map(entry => `${entry.raw_line}: ${entry.text}`)
  if (truncated) {
    lines.push(
      `Results were capped at ${AHP_DEBUG_LOG_MAX_LINES} entries. Request a narrower raw-line range to continue.`,
    )
  }

  return {
    success: true,
    pastTenseMessage: `Read ${entries.length} AHP timeline entries`,
    structuredContent,
    content: [textContent(lines.length > 0 ? lines.join('\n') : 'No timeline entries matched this request.')],
  }
}

function readLines(params: Record<string, unknown>, snapshot: DebugLogSnapshot): ToolCallResult {
  const {startLine, endLine} = parseRequiredRange(params, snapshot.lines.length)
  if (endLine - startLine + 1 > AHP_DEBUG_LOG_MAX_LINES) {
    throw new Error(`A raw-line request can include at most ${AHP_DEBUG_LOG_MAX_LINES} lines.`)
  }

  const lines = snapshot.lines.slice(startLine - 1, endLine).map((text, index) => ({
    line: startLine + index,
    text,
  }))
  return {
    success: true,
    pastTenseMessage: `Read AHP debug-log lines ${startLine}-${endLine}`,
    structuredContent: {
      snapshot_id: snapshot.id,
      range: {start_line: startLine, end_line: endLine},
      lines,
    },
    content: [textContent(lines.map(line => `${line.line}: ${line.text}`).join('\n'))],
  }
}

function requireSnapshot(value: unknown, state: AhpDebugLogToolState): DebugLogSnapshot {
  if (typeof value !== 'string' || !value) {
    throw new Error('snapshot_id is required for timeline and lines operations. Call overview first.')
  }
  const snapshot = state.snapshots.get(value)
  if (!snapshot) throw new Error(`Unknown or expired snapshot_id: ${value}. Call overview again.`)
  return snapshot
}

function parseTimelineMode(value: unknown): TimelineMode {
  if (value === undefined || value === 'important') return 'important'
  if (value === 'errors' || value === 'all') return value
  throw new Error('mode must be one of: important, errors, all')
}

function parseTerms(value: unknown): string[] {
  if (value === undefined) return []
  if (!Array.isArray(value) || value.length > 10 || value.some(term => typeof term !== 'string')) {
    throw new Error('terms must be an array of at most 10 strings')
  }
  return value.map(term => term.trim().toLowerCase()).filter(Boolean)
}

function parseOptionalRange(params: Record<string, unknown>, totalLines: number): {startLine: number; endLine: number} {
  if (params.start_line === undefined && params.end_line === undefined) {
    return {startLine: 1, endLine: totalLines}
  }
  return parseRequiredRange(params, totalLines)
}

function parseRequiredRange(params: Record<string, unknown>, totalLines: number): {startLine: number; endLine: number} {
  const startLine = params.start_line
  const endLine = params.end_line
  if (!Number.isInteger(startLine) || !Number.isInteger(endLine)) {
    throw new Error('start_line and end_line must both be integers')
  }
  if ((startLine as number) < 1 || (endLine as number) < (startLine as number) || (endLine as number) > totalLines) {
    throw new Error(`Requested range must fall within 1-${totalLines} and end at or after its start.`)
  }
  return {startLine: startLine as number, endLine: endLine as number}
}

function textContent(text: string): ToolResultTextContent {
  return {type: 'text', text} as ToolResultTextContent
}
